import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Divider,
} from '@mui/material'

import { showModal } from 'actions/app'
import { get, noop, filter, find, isEmpty } from 'es-toolkit/compat'
import {
  DataGrid,
  type GridRowId,
  type GridRowSelectionModel,
  type GridColDef,
  type GridComparatorFn,
} from '@mui/x-data-grid'

import { Switch, Stack, Typography } from '@mui/material'

import { getFarmsPaginated } from 'api/farm'
import colors from 'common/colors'
import {
  Edit as EditIcon,
  Search as SearchIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  DynamicFeedOutlined as DynamicFeedOutlinedIcon,
  ArchiveOutlined as ArchiveOutlinedIcon,
  UnarchiveOutlined as UnarchiveOutlinedIcon,
} from '@mui/icons-material'
import AddIcon from 'components/add-icon'
import { unitConvert } from '@farmtrx/units'

import OCFFAddFarmComponent from './add-farm'
import OCFFDeleteFarm from './delete-farm'
import ArchiveFarm from './archive-farm'
import RestoreFarms from './restoreFarm'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
  useRef,
} from 'react'

import type { AppState } from 'state/index'

import OCFFAssignFarmsComponent from './assign-farms-to-clients'

import { type FieldModel } from 'state/field'

declare module 'components/cff/farms/index.js' {
  type featuresOverviewType = {
    id: number
    geometry: object
    properties: object
  }

  type fieldAreaMapType = {
    id: number
    area: number
    farmId: number
  }

  type RowData = {
    id: number
    fieldArea: string | 0
    name: string | ''
    fieldCount: number | 0
    clientName: string | ''
    clientId: string | ''
  }
}
export default function OCFFFarmsComponent(): ReactElement<any, any> {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const fields: FieldModel[] = useSelector((state) =>
    get(state, 'field.collection', []),
  )
  const farms = useSelector((state) =>
    get(state as AppState, 'farm.collection', []),
  )
  const clients = useSelector((state) =>
    get(state as AppState, 'client.collection', []),
  )
  const userPrefs = useSelector((state) =>
    get(state as AppState, 'account.singleton.prefs'),
  )
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set<GridRowId>([]),
    })
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  const permissions = useSelector((state) =>
    get(state as AppState, 'account.permissions'),
  )

  const showFarms = permissions.includes('farm:read')
  const addModifyFarms = permissions.includes('farm:write')

  const [columns, setColumns] = useState<GridColDef[]>([])
  const [columnVisibility, setColumnVisibility] = useState({
    fullName: true,
  })
  const [rows, setRows] = useState<RowData[]>([])
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })
  const [rowCount, setRowCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [showActiveFarms, setShowActiveFarms] = useState(true)

  // Callback to reload DataGrid after assignment
  const reloadFarms = useCallback(() => {
    // This should trigger the same logic as your DataGrid reload (e.g., refetch or update state)
    setPaginationModel((prev) => ({ ...prev }))
  }, [])

  const onSearch = (e) => {
    setSearchValue(e.target.value)
  }

  // Debounce searchValue
  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchValue)
    }, 300)
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
    }
  }, [searchValue])

  const filterFarms = (searchValue, farm) => {
    if (searchValue) {
      const lowerSearchValue = searchValue.toLowerCase()
      return farm.name.toLowerCase().indexOf(lowerSearchValue) > -1
    }
    return true
  }

  const showAddFarmPopup = useCallback(() => {
    dispatch(
      showModal(
        <OCFFAddFarmComponent isEditOrAdd="add" onSuccess={reloadFarms} />,
      ),
    )
  }, [reloadFarms])
  const fieldAreaSort: GridComparatorFn<string> = (v1, v2) => {
    const area1 = parseFloat(v1.split(' ')[0]) || 0
    const area2 = parseFloat(v2.split(' ')[0]) || 0
    return area1 - area2
  }

  useEffect(() => {
    const _columns: GridColDef[] = [
      {
        field: 'name',
        headerName: t('farm'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
      },
      {
        field: 'clientName',
        headerName: t('client'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
      },
      {
        field: 'fieldCount',
        headerName: t('fields_quantity'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
      },
      {
        field: 'areaSqm',
        headerName: t('field_area'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
        sortComparator: fieldAreaSort,
      },
    ]
    setColumns(_columns)
    setRowSelectionModel({
      type: 'include',
      ids: new Set<GridRowId>([]),
    })
  }, [clients])

  function CustomNoRowsOverlayFields(): ReactElement {
    if (showFarms) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          {t('no_farms')}
        </div>
      )
    } else
      return (
        <div style={{ textAlign: 'center', padding: '32px' }}>
          {t('no_permissions', { item: t('clients') })}
        </div>
      )
  }

  const onChangeShowActiveFarms = useCallback(() => {
    setShowActiveFarms(!showActiveFarms)
  }, [showActiveFarms])

  const getClientName = useCallback(
    (id) => {
      const name = find(clients, ['id', id])?.['name']
      return name
    },
    [clients],
  )

  function fetchFarmsPaginated(
    page,
    pageSize,
    search,
    ignore = false,
    archived = 'false',
  ) {
    getFarmsPaginated(page, pageSize, search, undefined, archived)
      .then((res) => {
        if (!ignore) {
          const { results, pagination } = res.data
          // Map to DataGrid rows using server-provided values
          const _rows = (results || []).map((farm) => ({
            id: farm.id,
            areaSqm: farm.areaSqm
              ? `${unitConvert
                  .convertAreaToPref(
                    farm.areaSqm,
                    userPrefs['units']['area_in'],
                  )
                  .toFixed(2)} ${userPrefs['units']['area_in']}`
              : `0 ${userPrefs?.['units']['area_in']}`,
            name: farm.name,
            clientName:
              farm.clientName ?? getClientName(farm['clientId']) ?? '',
            fieldCount: farm.fieldCount ?? 0,
            clientId: farm.clientId,
          }))
          setRows(_rows)
          setRowCount(pagination?.total || 0)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    let ignore = false
    setLoading(true)
    if (showFarms) {
      fetchFarmsPaginated(
        (paginationModel.page || 0) + 1,
        paginationModel.pageSize,
        debouncedSearch,
        ignore,
        showActiveFarms ? 'false' : 'true',
      )
    } else {
      setRows([])
      setRowCount(0)
      setLoading(false)
    }
    return () => {
      ignore = true
    }
  }, [paginationModel, debouncedSearch, showFarms, userPrefs, showActiveFarms])

  const editFarm = useCallback(() => {
    if (rowSelectionModel.ids.size) {
      const selectedId = rowSelectionModel.ids.values().next().value
      const selectedFarm = find(rows, ['id', selectedId])
      dispatch(
        showModal(
          <OCFFAddFarmComponent
            isEditOrAdd="edit"
            selectedFarm={selectedFarm}
            onSuccess={reloadFarms}
          />,
        ),
      )
    }
  }, [rowSelectionModel, reloadFarms])

  const deleteFarm = useCallback(() => {
    if (rowSelectionModel.ids.size) {
      dispatch(
        showModal(
          <OCFFDeleteFarm
            selectedFarms={filter(rows, (row) =>
              rowSelectionModel.ids.has(row.id),
            )}
            onSuccess={reloadFarms}
          />,
        ),
      )
    }
  }, [rowSelectionModel, reloadFarms])

  const archiveFarm = useCallback(() => {
    if (rowSelectionModel.ids.size) {
      dispatch(
        showModal(
          <ArchiveFarm
            selectedFarms={filter(rows, (row) =>
              rowSelectionModel.ids.has(row.id),
            )}
            onSuccess={reloadFarms}
          />,
        ),
      )
    }
  }, [rowSelectionModel, reloadFarms])

  const restoreFarm = useCallback(() => {
    if (rowSelectionModel.ids.size) {
      dispatch(
        showModal(
          <RestoreFarms
            selectedFarmIds={Array.from(rowSelectionModel.ids)}
            callback={reloadFarms}
          />,
        ),
      )
    }
  }, [rowSelectionModel, reloadFarms])

  const assignFarms = useCallback(() => {
    const selectedFarms = []
    const selectedIds = Array.from(rowSelectionModel.ids)
    selectedIds.forEach((s) => {
      const obj = find(rows, ['id', s])
      //@ts-ignore
      selectedFarms.push(obj)
    })
    dispatch(
      showModal(
        <OCFFAssignFarmsComponent
          selectedFarms={selectedFarms}
          selectedIds={selectedIds}
          onFarmsAssigned={reloadFarms}
        />,
      ),
    )
  }, [rowSelectionModel, reloadFarms, rows])

  const onClick = noop

  return (
    <Box
      sx={{
        px: 3,
        py: 4,
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h1>{t('farms')}</h1>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <Typography
              sx={{
                fontSize: '0.8rem',
              }}
            >
              {t('archived')}
            </Typography>
            <Switch
              size="small"
              checked={showActiveFarms}
              onChange={onChangeShowActiveFarms}
            />
            <Typography
              sx={{
                fontSize: '0.8rem',
                marginRight: '10px',
              }}
            >
              {t('active')}
            </Typography>
          </Stack>
          <Divider orientation="vertical" flexItem />
          {showActiveFarms ? (
            <>
              <IconButton
                style={{ display: addModifyFarms ? 'inline-flex' : 'none' }}
                disabled={rowSelectionModel.ids.size === 0}
                onClick={archiveFarm}
                title={t('archive_item', { item: t('farm') })}
                size="large"
              >
                <ArchiveOutlinedIcon
                  style={{
                    color:
                      rowSelectionModel.ids.size >= 1 ? colors.brand : 'gray',
                  }}
                />
              </IconButton>

              <IconButton
                style={{ display: addModifyFarms ? 'inline-flex' : 'none' }}
                title={t('assign_farms_to_client')}
                onClick={assignFarms}
                disabled={
                  rowSelectionModel.ids.size === 0 ||
                  (rowSelectionModel.ids.size > 1 && rows.length === 1)
                }
                size="large"
              >
                <DynamicFeedOutlinedIcon
                  style={{
                    color: rowSelectionModel.ids.size ? colors.brand : 'gray',
                  }}
                />
              </IconButton>

              <IconButton
                style={{ display: addModifyFarms ? 'inline-flex' : 'none' }}
                title={t('edit_farm')}
                disabled={
                  rowSelectionModel.ids.size === 0 ||
                  rowSelectionModel.ids.size > 1
                }
                onClick={editFarm}
                size="large"
              >
                <EditIcon
                  style={{
                    color:
                      rowSelectionModel.ids.size === 1 ? colors.brand : 'gray',
                  }}
                />
              </IconButton>

              <IconButton
                style={{ display: addModifyFarms ? 'inline-flex' : 'none' }}
                onClick={showAddFarmPopup}
                title={t('add_farm')}
                size="large"
              >
                <AddIcon />
              </IconButton>
            </>
          ) : (
            <>
              {' '}
              <IconButton
                style={{ display: addModifyFarms ? 'inline-flex' : 'none' }}
                disabled={rowSelectionModel.ids.size === 0}
                onClick={deleteFarm}
                title={t('delete_item', {
                  item: rowSelectionModel.ids.size > 1 ? t('farms') : t('farm'),
                })}
                size="large"
              >
                <DeleteOutlineOutlinedIcon
                  style={{
                    color:
                      rowSelectionModel.ids.size >= 1 ? colors.brand : 'gray',
                  }}
                />
              </IconButton>
              <IconButton
                style={{ display: addModifyFarms ? 'inline-flex' : 'none' }}
                disabled={rowSelectionModel.ids.size === 0}
                onClick={restoreFarm}
                title={t('restore_item', {
                  item: rowSelectionModel.ids.size > 1 ? t('farms') : t('farm'),
                })}
                size="large"
              >
                <UnarchiveOutlinedIcon
                  style={{
                    color:
                      rowSelectionModel.ids.size > 0 ? colors.brand : 'gray',
                  }}
                />
              </IconButton>
            </>
          )}
          <TextField
            id="search"
            value={searchValue}
            onChange={onSearch}
            placeholder={t('search')}
            size="small"
            sx={{ marginLeft: 2, minWidth: 200 }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: `${Math.max(400, paginationModel.pageSize * 5)}px`,
          height: '100%',
          overflow: 'auto',
          paddingTop: 2,
        }}
      >
        <DataGrid
          rows={rows}
          loading={loading}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(selection: GridRowSelectionModel) => {
            if (selection?.type === 'exclude' && selection?.ids.size === 0) {
              // Setting to include all rows when "select all" is chosen, since DataGrid doesn't provide the list of all row IDs in this case
              // which caused some inconsistencies with the selection state. The rows showed as selected but the selection model was empty,
              // which caused issues with the archive/restore and delete actions.
              const allIds = new Set(rows.map((row) => row.id))
              setRowSelectionModel({ type: 'include', ids: allIds })
            } else {
              setRowSelectionModel(selection)
            }
          }}
          checkboxSelection
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          columnVisibilityModel={columnVisibility}
          onColumnVisibilityModelChange={(model: { fullName: true }) =>
            setColumnVisibility(model)
          }
          initialState={{
            sorting: {
              sortModel: [{ field: 'name', sort: 'asc' }],
            },
          }}
          slots={{
            noRowsOverlay: CustomNoRowsOverlayFields,
          }}
          columns={columns}
          pageSizeOptions={[10, 50, 100]}
          onRowClick={(params) => onClick(params.row)}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: colors.greyBackground,
              textTransform: 'uppercase',
            },
          }}
        />
      </Box>
    </Box>
  )
}
